/**
 * Visual evidence block for content pages.
 *
 * Renders real work-sample photographs with descriptive captions plus an
 * ImageObject / ImageGallery JSON-LD graph so Google Images and AI answer
 * engines can attribute each photo to this page. Presentational only.
 */

import { SmartImage } from "@/components/SmartImage";
import { getPageImages } from "@/lib/page-images";
import { getActiveSiteUrl } from "@/lib/seo-helpers";
import { COMPANY } from "@/lib/company";

export function PageGallery({
  category,
  seed = "",
  limit = 3,
  locale = "th",
  title,
  note,
}: {
  category: string | undefined;
  seed?: string;
  limit?: number;
  locale?: "th" | "en";
  title?: string;
  note?: string;
}) {
  const images = getPageImages(category, seed, limit);
  if (images.length === 0) return null;

  const site = getActiveSiteUrl();
  const heading =
    title ?? (locale === "th" ? "ตัวอย่างงานจริงของสำนักงาน" : "Real work samples from our office");
  const subline =
    note ??
    (locale === "th"
      ? "ภาพถ่ายจากงานที่สำนักงานดำเนินการจริง ปิดทับข้อมูลส่วนบุคคลของลูกค้าทุกจุดก่อนเผยแพร่"
      : "Photographs of work actually completed by our office. All client personal data is redacted before publication.");

  const toAbsolute = (src: string) => (src.startsWith("http") ? src : `${site}${src}`);

  const ld = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    name: heading,
    image: images.map((img) => ({
      "@type": "ImageObject",
      contentUrl: toAbsolute(img.src),
      url: toAbsolute(img.src),
      caption: img.caption,
      description: img.alt,
      width: img.width,
      height: img.height,
      creditText: COMPANY.legalName ?? COMPANY.brandName,
      creator: { "@type": "Organization", name: COMPANY.brandName, "@id": `${site}/#organization` },
      copyrightNotice: COMPANY.brandName,
    })),
  };

  return (
    <section aria-label={heading} className="scroll-mt-24">
      <h2 className="text-2xl font-black text-primary md:text-3xl">{heading}</h2>
      <p className="mt-3 text-sm text-muted-foreground">{subline}</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {images.map((img) => (
          <figure
            key={img.src}
            className="overflow-hidden rounded-2xl border border-border bg-card"
          >
            <SmartImage
              src={img.src}
              alt={img.alt}
              width={img.width}
              height={img.height}
              className="h-auto w-full object-cover"
              sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            />
            <figcaption className="border-t border-border px-4 py-3 text-xs leading-relaxed text-muted-foreground">
              {img.caption}
            </figcaption>
          </figure>
        ))}
      </div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
    </section>
  );
}